// Atmospheric clouds over the board (pure layout, climate cover, perf guard). Rendering lives in
// AtmosphericClouds.tsx (instanced puffs) and the soft moving shadows in the terrain shader
// (cloudShadow.ts uniforms). Everything is seeded by index through hexNoise: same map, same sky.
//
// Why high and sparse: the default camera sits at y 8.5 looking down at ~33 degrees, so a layer at
// y 7..9.5 shows up as drifting banks near the horizon and never sits between the camera and a tile;
// puffs near the camera and the whole layer when the camera zooms above it fade out (see the shader in
// AtmosphericClouds.tsx). The ground shadows do the visible work over the board.

import { hexNoise } from './elevation.js';

export const CLOUD_LAYER_MIN_Y = 7;
export const CLOUD_LAYER_MAX_Y = 9.5;
export const MAX_PUFFS = 36;
/** Wind in world units per second (x, z): slow, about one hex per 16 s. */
export const CLOUD_WIND: readonly [number, number] = [0.06, 0.02];

/**
 * Share of the sky covered, by map climate: dense in wet climates, sparse in desert. Unknown or missing
 * climates are temperate.
 */
export function cloudCover(climate?: string): number {
  switch (climate) {
    case 'tropical':
      return 0.62;
    case 'arctic':
      return 0.42;
    case 'desert':
      return 0.05;
    default:
      return 0.34;
  }
}

/** How many puffs to draw (the desert keeps a couple so the sky is not empty). */
export function cloudCount(climate?: string): number {
  return Math.max(2, Math.round(MAX_PUFFS * cloudCover(climate)));
}

/** Darkening of the terrain under cloud shadow (0..1). Light, so units and tiles stay readable. */
export function cloudShadowStrength(climate?: string): number {
  return Math.min(0.38, 0.18 + 0.3 * cloudCover(climate));
}

export interface CloudPuff {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly scale: number;
  /** 0.7..1.3: puffs drift at slightly different speeds. */
  readonly speed: number;
}

export type CloudBounds = readonly [number, number, number, number]; // minX, maxX, minZ, maxZ

const PAD = 10;

/** Deterministic starting layout of `count` puffs over (and around) the board. */
export function cloudLayout(bounds: CloudBounds, count: number): CloudPuff[] {
  const [minX, maxX, minZ, maxZ] = bounds;
  const w = maxX - minX + PAD * 2;
  const d = maxZ - minZ + PAD * 2;
  return Array.from({ length: count }, (_, i) => ({
    x: minX - PAD + hexNoise(i * 1.7, 31) * w,
    y: CLOUD_LAYER_MIN_Y + hexNoise(i * 2.3, 32) * (CLOUD_LAYER_MAX_Y - CLOUD_LAYER_MIN_Y),
    z: minZ - PAD + hexNoise(i * 3.1, 33) * d,
    scale: 1.1 + hexNoise(i * 1.3, 34) * 1.5,
    speed: 0.7 + hexNoise(i * 2.9, 35) * 0.6,
  }));
}

function wrap(v: number, lo: number, hi: number): number {
  const span = hi - lo;
  return lo + ((((v - lo) % span) + span) % span);
}

/**
 * Position of a puff at time `t` seconds. Drifts with the wind and wraps around the padded board, so the
 * sky never empties. `reducedMotion` freezes time (a static sky).
 */
export function cloudPosition(
  puff: CloudPuff,
  t: number,
  bounds: CloudBounds,
  reducedMotion = false,
): [number, number, number] {
  const tt = reducedMotion ? 0 : t;
  const [minX, maxX, minZ, maxZ] = bounds;
  return [
    wrap(puff.x + CLOUD_WIND[0] * puff.speed * tt, minX - PAD, maxX + PAD),
    puff.y,
    wrap(puff.z + CLOUD_WIND[1] * puff.speed * tt, minZ - PAD, maxZ + PAD),
  ];
}

/**
 * Performance guard for low-end GPUs: when the smoothed frame rate stays under `minFps` for `graceSec`
 * of wall time, the clouds and their shadows switch themselves off for the rest of the session (sticky,
 * so they do not flicker on and off). The first seconds after mount are ignored (shader compiles).
 */
export class CloudPerfGuard {
  private slowSec = 0;
  private elapsed = 0;
  private off = false;

  constructor(
    private readonly minFps = 24,
    private readonly graceSec = 4,
    private readonly warmupSec = 6,
  ) {}

  /** Feed one sample: the current fps estimate and the seconds since the last sample. */
  sample(fps: number, dtSec: number): void {
    if (this.off) return;
    this.elapsed += dtSec;
    if (this.elapsed < this.warmupSec || fps <= 0) return;
    this.slowSec = fps < this.minFps ? this.slowSec + dtSec : 0;
    if (this.slowSec >= this.graceSec) this.off = true;
  }

  get degraded(): boolean {
    return this.off;
  }
}
